// Общая рабочая область оркестратора: круг-индикатор, кнопки, лента
// транскрипта, поле «написать текстом», карточка ключа. Её монтируют и
// десктопный раздел, и мобильный стек-экран — логика целиком в store.ts.

import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Icon } from '../../ui/Icon'
import { isLive, levels, statusText, useOrchestrator } from './store'
import type { OrchItem, OrchStatus } from './store'
import './orchestrator.css'

// ---------------- круг ----------------

/** Круг-индикатор. Пульс считается в requestAnimationFrame и пишется прямо в
 *  CSS-переменную: 60 перерисовок React в секунду ради масштаба ни к чему. */
export function Orb({ status, size = 200 }: { status: OrchStatus; size?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (status !== 'listening' && status !== 'speaking') {
      el.style.setProperty('--lvl', '0')
      return
    }
    let raf = 0
    let smooth = 0
    const tick = () => {
      const { mic, out } = levels()
      // хвост ответа доигрывает и после того, как сервер вернулся в «слушаю»
      const target = Math.min(1, (status === 'speaking' ? out : Math.max(mic, out)) * 5)
      // быстро вверх, плавно вниз — так пульс читается как голос, а не дрожь
      smooth = target > smooth ? smooth * 0.5 + target * 0.5 : smooth * 0.88 + target * 0.12
      el.style.setProperty('--lvl', smooth.toFixed(3))
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [status])
  return (
    <div className={`abx-orb is-${status}`} style={{ width: size, height: size }} ref={ref} aria-hidden>
      <div className="abx-orb__halo" />
      <div className="abx-orb__core" />
    </div>
  )
}

// ---------------- лента ----------------

function ItemLine({ item, onOpenSession }: { item: OrchItem; onOpenSession?: (id: string) => void }) {
  if (item.kind === 'tool') {
    const sid = item.tool?.sessionId
    const cls = `abx-orch__tool${item.tool?.status === 'error' ? ' is-error' : ''}${item.tool?.status === 'start' ? ' is-run' : ''}`
    if (sid && onOpenSession) {
      return (
        <button type="button" className={`${cls} is-link`} onClick={() => onOpenSession(sid)} title="открыть беседу">
          ⎿ {item.text}
        </button>
      )
    }
    return <div className={cls}>⎿ {item.text}</div>
  }
  return (
    <div className={`abx-orch__msg is-${item.kind}${item.final ? '' : ' is-live'}`}>
      <span className="abx-orch__who">{item.kind === 'user' ? 'Вы' : 'Ассистент'}</span>
      <span className="abx-orch__text">{item.text}</span>
    </div>
  )
}

export function Transcript({ items, empty, onOpenSession }: {
  items: OrchItem[]
  empty?: string
  onOpenSession?: (id: string) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const last = items[items.length - 1]
  // Прилипаем к низу, пока человек сам не отмотал ленту вверх.
  useEffect(() => {
    const el = ref.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [items.length, last?.text])
  return (
    <div
      className="abx-orch__feed"
      ref={ref}
      onScroll={(e) => {
        const el = e.currentTarget
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
      }}
    >
      {items.length === 0 && empty ? <div className="abx-orch__empty">{empty}</div> : null}
      {items.map((item) => <ItemLine key={item.id} item={item} onOpenSession={onOpenSession} />)}
    </div>
  )
}

// ---------------- ключ ----------------

export function KeyCard() {
  const saveKey = useOrchestrator((s) => s.saveKey)
  const keyError = useOrchestrator((s) => s.keyError)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy || !value.trim()) return
    setBusy(true)
    const ok = await saveKey(value)
    setBusy(false)
    if (ok) setValue('')
  }
  return (
    <form className="abx-orch__key" onSubmit={(e) => void submit(e)}>
      <div className="abx-orch__key-title">Вставьте ключ OpenAI API</div>
      <p className="abx-orch__key-lead">
        Голос идёт через OpenAI (GPT-Live или Realtime). Ключ хранится на сервере панели и в браузер
        больше не возвращается.
      </p>
      <label className="abx-orch__key-row">
        <span>Ключ OpenAI API</span>
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder="sk-…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      {keyError ? <div className="abx-orch__err">{keyError}</div> : null}
      <button type="submit" className="abx-orch__btn is-primary" disabled={busy || !value.trim()}>
        {busy ? 'сохраняю…' : 'Сохранить'}
      </button>
    </form>
  )
}

// ---------------- голос ----------------

/** Компактный выбор голоса: сохраняется в профиле, звучит со следующего запуска
 *  (OpenAI не меняет голос, когда ассистент уже говорил в этой сессии). */
function VoicePicker() {
  const voice = useOrchestrator((s) => s.key?.voice)
  const voices = useOrchestrator((s) => s.key?.voices)
  const pending = useOrchestrator((s) => s.voicePending)
  const setVoice = useOrchestrator((s) => s.setVoice)
  if (!voice || !voices?.length) return null
  const list = voices.includes(voice) ? voices : [voice, ...voices]
  return (
    <label className="abx-orch__voice" title="голос ассистента — применяется со следующего «Запустить»">
      <span>голос</span>
      <select value={voice} onChange={(e) => void setVoice(e.target.value)}>
        {list.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      {pending ? <span className="abx-orch__voice-note">со следующего запуска</span> : null}
    </label>
  )
}

// ---------------- движок и мозг ----------------

const ENGINE_LABEL: Record<string, string> = { live: 'GPT-Live + мозги Claude', realtime: 'Realtime (v2)' }

/** Движок голоса и модели мозгов (первая линия, планировщик); ссылки на их беседы. */
function BrainControls({ onOpenSession }: { onOpenSession?: (id: string) => void }) {
  const key = useOrchestrator((s) => s.key)
  const setEngine = useOrchestrator((s) => s.setEngine)
  const setBrainModel = useOrchestrator((s) => s.setBrainModel)
  if (!key?.engine) return null
  const live = key.engine === 'live'
  return (
    <div className="abx-orch__brain">
      {key.engines?.length ? (
        <label className="abx-orch__voice" title="движок голоса — применяется со следующего «Запустить»">
          <span>движок</span>
          <select value={key.engine} onChange={(e) => void setEngine(e.target.value)}>
            {key.engines.map((v) => <option key={v} value={v}>{ENGINE_LABEL[v] ?? v}</option>)}
          </select>
        </label>
      ) : null}
      {live && key.brainModels?.length ? (
        <label className="abx-orch__voice" title="первая линия — принимает каждый вопрос голоса; модель меняется сразу">
          <span>быстрый</span>
          <select value={key.frontModel} onChange={(e) => void setBrainModel('front', e.target.value)}>
            {key.brainModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
      ) : null}
      {live && key.brainModels?.length ? (
        <label className="abx-orch__voice" title="модель проектов — каждая большая задача (исследование, план, разработка) идёт отдельной беседой «Проект: …»; действует на новые проекты">
          <span>модель проектов</span>
          <select value={key.plannerModel} onChange={(e) => void setBrainModel('planner', e.target.value)}>
            {key.brainModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
      ) : null}
      {live && key.frontSessionId && onOpenSession ? (
        <button type="button" className="abx-orch__link" onClick={() => onOpenSession(key.frontSessionId as string)}>
          открыть быстрый в «Беседах»
        </button>
      ) : null}
      {live && key.plannerSessionId && onOpenSession ? (
        <button type="button" className="abx-orch__link" onClick={() => onOpenSession(key.plannerSessionId as string)}>
          прежний планировщик в «Беседах»
        </button>
      ) : null}
      {key.inbox ? (
        <span className="abx-orch__inbox" title="обновления рабочих бесед, пока разговор не был открыт — мозг получит их при запуске">
          обновлений: {key.inbox}
        </span>
      ) : null}
    </div>
  )
}

// ---------------- проекты ----------------

const PROJECT_DOT: Record<string, string> = {
  planning: 'is-run', awaiting_approval: 'is-wait', running: 'is-run', done: 'is-done', paused: 'is-paused',
}

/** Проекты (v5): большая задача = беседа «Проект: …»; клик — открыть её. */
function Projects({ onOpenSession }: { onOpenSession?: (id: string) => void }) {
  const list = useOrchestrator((s) => s.key?.projects)
  const engine = useOrchestrator((s) => s.key?.engine)
  const [all, setAll] = useState(false)
  if (engine !== 'live' || !list?.length) return null
  const open = list.filter((p) => p.status !== 'done')
  const shown = all ? list : open
  return (
    <div className="abx-orch__projects">
      <div className="abx-orch__projects-head">
        <span>Проекты{open.length ? ` · ${open.length}` : ''}</span>
        {list.length > open.length ? (
          <button type="button" className="abx-orch__link" onClick={() => setAll(!all)}>
            {all ? 'только открытые' : `и завершённые (${list.length - open.length})`}
          </button>
        ) : null}
      </div>
      {shown.length === 0 ? <div className="abx-orch__projects-empty">открытых проектов нет</div> : null}
      {shown.map((p) => (
        <button
          key={p.id}
          type="button"
          className="abx-orch__project"
          disabled={!p.sessionId || !onOpenSession}
          onClick={() => p.sessionId && onOpenSession?.(p.sessionId)}
          title={p.planPath ? `план: ${p.planPath}` : 'открыть беседу проекта'}
        >
          <span className={`abx-orch__project-dot ${PROJECT_DOT[p.status] ?? ''}`} aria-hidden />
          <span className="abx-orch__project-title">{p.title}</span>
          <span className="abx-orch__project-status">{p.statusRu}{p.state === 'exited' ? ' · беседа остановлена' : ''}</span>
        </button>
      ))}
    </div>
  )
}

// ---------------- ввод текстом ----------------

function Composer({ live }: { live: boolean }) {
  const sendText = useOrchestrator((s) => s.sendText)
  const [value, setValue] = useState('')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (sendText(value)) setValue('')
  }
  return (
    <form className="abx-orch__compose" onSubmit={submit}>
      <input
        type="text"
        value={value}
        disabled={!live}
        placeholder={live ? 'написать текстом…' : 'запустите разговор, чтобы написать'}
        onChange={(e) => setValue(e.target.value)}
        enterKeyHint="send"
      />
      <button type="submit" className="abx-orch__btn" disabled={!live || !value.trim()}>отправить</button>
    </form>
  )
}

// ---------------- вся область ----------------

interface PanelProps {
  /** открыть беседу, которой оркестратор поручил задачу */
  onOpenSession?: (sessionId: string) => void
  /** телефонная вёрстка: круг меньше, лента короче */
  compact?: boolean
}

export function OrchestratorPanel({ onOpenSession, compact }: PanelProps) {
  const status = useOrchestrator((s) => s.status)
  const detail = useOrchestrator((s) => s.detail)
  const error = useOrchestrator((s) => s.error)
  const muted = useOrchestrator((s) => s.muted)
  const autoMuted = useOrchestrator((s) => s.autoMuted)
  const items = useOrchestrator((s) => s.items)
  const key = useOrchestrator((s) => s.key)
  const keyError = useOrchestrator((s) => s.keyError)
  const viewing = useOrchestrator((s) => s.viewing)
  const viewItems = useOrchestrator((s) => s.viewItems)
  const viewLoading = useOrchestrator((s) => s.viewLoading)
  const history = useOrchestrator((s) => s.history)
  const { start, stop, toggleMute, closeHistory, loadStatus } = useOrchestrator.getState()

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  const live = isLive(status)
  const noKey = key !== null && !key.hasKey

  if (viewing) {
    const entry = history.find((h) => h.id === viewing)
    return (
      <div className={`abx-orch${compact ? ' is-compact' : ''}`}>
        <div className="abx-orch__past">
          <button type="button" className="abx-orch__btn" onClick={closeHistory}>
            ← {live ? 'к текущему разговору' : 'назад'}
          </button>
          <span className="abx-orch__past-title">разговор {entry?.when ?? viewing}</span>
        </div>
        <Transcript
          items={viewItems}
          empty={viewLoading ? 'загружаю…' : 'в этом разговоре нет реплик'}
          onOpenSession={onOpenSession}
        />
      </div>
    )
  }

  return (
    <div className={`abx-orch${compact ? ' is-compact' : ''}`}>
      <div className="abx-orch__stage">
        {noKey ? <KeyCard /> : (
          <>
            <Orb status={status} size={compact ? 150 : 200} />
            <div className={`abx-orch__status is-${status}`}>{statusText(status, detail, muted, autoMuted)}</div>
            <div className="abx-orch__controls">
              {live ? (
                <button type="button" className="abx-orch__btn is-stop" onClick={stop}>Завершить</button>
              ) : (
                <button
                  type="button"
                  className="abx-orch__btn is-primary"
                  disabled={key === null && !keyError}
                  onClick={start}
                >
                  Запустить
                </button>
              )}
              <button
                type="button"
                className={`abx-orch__btn is-icon${muted ? ' is-muted' : ''}`}
                disabled={!live}
                onClick={toggleMute}
                title={autoMuted ? 'выключен на время записи голосового — нажмите, чтобы включить' : muted ? 'включить микрофон' : 'выключить микрофон'}
                aria-label={muted ? 'включить микрофон' : 'выключить микрофон'}
                aria-pressed={muted}
              >
                <Icon name="mic" size={18} />
                {muted ? <span className="abx-orch__slash" aria-hidden /> : null}
              </button>
            </div>
            <VoicePicker />
            <BrainControls onOpenSession={onOpenSession} />
            <Projects onOpenSession={onOpenSession} />
            {error ? <div className="abx-orch__err">{error}</div> : null}
            {keyError && key === null ? <div className="abx-orch__err">не узнал статус оркестратора: {keyError}</div> : null}
          </>
        )}
      </div>
      {noKey && items.length === 0 ? null : (
        <Transcript
          items={items}
          empty="Нажмите «Запустить» и спросите, например: «как дела?» или «что у меня сегодня?»"
          onOpenSession={onOpenSession}
        />
      )}
      {noKey ? null : <Composer live={live && status !== 'connecting'} />}
    </div>
  )
}
